import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { FaCrown, FaHeart, FaCalendarAlt, FaExclamationTriangle, FaRocket, FaGift } from 'react-icons/fa';

/**
 * Modal amigable para recordar al usuario VIP que su plan venció
 */
export function VIPPlanExpiredModal({ isOpen, onClose, userName, daysOverdue, onViewPlans }) {
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return '¡Buenos días';
    if (hour < 18) return '¡Buenas tardes';
    return '¡Buenas noches';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200">
        <DialogHeader className="text-center">
          <div className="mx-auto w-20 h-20 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full flex items-center justify-center mb-4 animate-pulse">
            <FaHeart className="text-white text-3xl" />
          </div>
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            {getGreeting()}, {userName?.split(' ')[0]}! 💕
          </DialogTitle>
          <DialogDescription className="text-gray-600 text-base mt-2">
            ¡Qué alegría verte de nuevo por aquí!
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="bg-white/80 rounded-xl p-4 border border-purple-100">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <FaCalendarAlt className="text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Tu membresía necesita renovación</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Han pasado <span className="font-bold text-amber-600">{daysOverdue || 'algunos'}</span> días desde que tu plan venció.
                  Queremos seguir siendo parte de tu transformación. 💪
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl p-4">
            <p className="text-center text-purple-700 font-medium">
              🌟 Tu bienestar es nuestra prioridad 🌟
            </p>
            <p className="text-sm text-center text-gray-600 mt-2">
              Renueva tu plan para continuar con tus entrenamientos, 
              dietas personalizadas y todo el contenido exclusivo.
            </p>
          </div>
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-col">
          <Button 
            onClick={onViewPlans}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-3"
          >
            <FaRocket className="mr-2" /> Renovar Mi Plan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Modal cuando el precio especial del usuario VIP ha expirado (6+ meses inactivo)
 */
export function VIPSpecialPriceExpiredModal({ isOpen, onClose, userName, onViewPlans }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200">
        <DialogHeader className="text-center">
          <div className="mx-auto w-20 h-20 bg-gradient-to-r from-amber-400 to-orange-400 rounded-full flex items-center justify-center mb-4">
            <FaCrown className="text-white text-3xl" />
          </div>
          <DialogTitle className="text-2xl font-bold text-amber-700">
            ¡Te extrañamos, {userName?.split(' ')[0]}! 😢
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="bg-white/80 rounded-xl p-4 border border-amber-100">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <FaExclamationTriangle className="text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Tu precio especial ha expirado</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Lamentamos informarte que, debido a la inactividad de más de 6 meses, 
                  tu precio especial ya no está vigente.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl p-4">
            <p className="text-center font-medium text-amber-800">
              ¡Pero no te preocupes! 💛
            </p>
            <p className="text-sm text-center text-gray-600 mt-2">
              Te invitamos a volver con nosotros con alguno de los planes 
              que tenemos disponibles especialmente para ti.
            </p>
          </div>

          <div className="bg-white rounded-xl p-3 border border-green-200">
            <p className="text-center text-sm text-green-700">
              <FaGift className="inline mr-1" />
              ¡Tenemos ofertas increíbles esperándote!
            </p>
          </div>
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-col">
          <Button 
            onClick={onViewPlans}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-3"
          >
            <FaCrown className="mr-2" /> Ver Planes Disponibles
          </Button>
          <Button 
            variant="ghost" 
            onClick={onClose}
            className="w-full text-gray-500 hover:text-gray-700"
          >
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Modal de bienvenida para usuario VIP con plan activo
 */
export function VIPWelcomeModal({ isOpen, onClose, userName, daysRemaining, planType }) {
  const getPlanEmoji = () => {
    switch(planType) {
      case 'gold': return '🥇';
      case 'premium': return '🥈';
      default: return '🥉';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200">
        <DialogHeader className="text-center">
          <div className="mx-auto w-20 h-20 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full flex items-center justify-center mb-4">
            <span className="text-4xl">{getPlanEmoji()}</span>
          </div>
          <DialogTitle className="text-2xl font-bold text-green-700">
            ¡Bienvenid@, {userName?.split(' ')[0]}! 🌟
          </DialogTitle>
          <DialogDescription className="text-gray-600 text-base mt-2">
            Tu membresía VIP está activa
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="bg-white/80 rounded-xl p-4 border border-green-100 text-center">
            <p className="text-sm text-gray-500">Días restantes de tu plan</p>
            <p className="text-4xl font-bold text-green-600 my-2">{daysRemaining}</p>
            <p className="text-sm text-gray-600">
              ¡Sigue así, vas increíble! 💪
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button 
            onClick={onClose}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold"
          >
            ¡A entrenar! 🏋️
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default {
  VIPPlanExpiredModal,
  VIPSpecialPriceExpiredModal,
  VIPWelcomeModal
};
